import { RightSideModal, RightSideModalTitle } from 'components';
import { IAppointmentWithCustomer } from 'models/shop/appointments';
import * as c from 'models/constants';
import * as React from 'react';
import { IStore } from 'redux/IStore';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { ITranslateProps, InjectedFormProps } from 'models/common';
import { InjectedManualBookingModalProps, ManualBookingScreen } from '../';
import { translate } from 'react-i18next';
import { IManualBookingFormData, IManualBookingFrequency, ManualBookingPeriods } from 'models/shop/manualbooking';
import { RightSideModalFooter } from 'components/RightSideModal';
import { addURLSearchParams, timePickerProps } from 'helpers/common';
import moment = require('moment');
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { Field } from 'redux-form';
import { required } from 'helpers/validationattributes';
import { BookingDatePicker, BookingSelectMenu } from 'components/FormControls';
import { generateEndItems, generateFrequencyItems, generateNewBookingItemTimes, getTimeUnit } from '../helpers';
require('./style.scss');

interface IOwnProps {
	appointment: IAppointmentWithCustomer;
	officeId?: string;
}

interface IState {
	initialRecurringEventFrequency: string;
	initialRecurringEventEndsAt: number;
	initialStartTime: string;
}

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ITranslateProps &
	InjectedFormProps &
	InjectedManualBookingModalProps;

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class RecurringEventScreenComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.handleDateTimeChange = this.handleDateTimeChange.bind(this);

		this.state = {
			initialRecurringEventEndsAt: undefined,
			initialRecurringEventFrequency: '',
			initialStartTime: '',
		};
	}

	componentDidMount(): void {
		if (this.props.shopOrderCard?.isFetching) {
			this.props.setScreen(ManualBookingScreen.Main);
		}

		if (!this.props.currentFormValues?.frequency && !this.props.currentFormValues?.endsAt) {
			this.props.change('frequency', JSON.stringify({ multiplier: 0, period: ManualBookingPeriods.DoesNotRepeat }));
		}

		this.setState({
			initialRecurringEventFrequency: this.props.currentFormValues?.frequency || '',
			initialRecurringEventEndsAt: this.props.currentFormValues?.endsAt || undefined,
			initialStartTime: this.props.currentFormValues?.startTimeOfTheAppointment || '',
		});
	}

	handleDateTimeChange(value: string, type: 'time' | 'date') {
		const oldDateTime = momentWithTimezone(this.props.timezone, this.props.time);
		const newDateTime = momentWithTimezone(this.props.timezone, value);

		let newDate: moment.Moment;
		let newTimeUnix: number;

		// If the date changed then take the old time and new date
		if (type === 'date') {
			newDate = newDateTime.startOf('day');
			newTimeUnix = oldDateTime.unix() - oldDateTime.startOf('day').unix();
		} else {
			newDate = oldDateTime.startOf('day');
			newTimeUnix = newDateTime.unix() - newDateTime.startOf('day').unix();
		}

		newDate = newDate.clone().add(newTimeUnix, 'seconds');

		// Relatively update time of booking items (send new and old date, first booking item gets the new date, others get newDate + difference between newDate and oldDate)
		if (this.props.currentFormValues?.bookingItems?.length) {
			const oldDate = moment(this.props.time);
			const updatedBookingItems = generateNewBookingItemTimes(
				this.props.currentFormValues.bookingItems,
				newDate,
				oldDate,
			);

			this.props.change('bookingItems', updatedBookingItems);
		}

		addURLSearchParams('time', newDate.toISOString());
	}

	public render() {
		const { t, timezone, currentFormValues } = this.props;
		const { frequency, endsAt, endsAtDate } = currentFormValues;
		let isRightButtonDisabled = false;

		if (
			frequency !== JSON.stringify({ multiplier: 0, period: ManualBookingPeriods.DoesNotRepeat }) &&
			(!frequency || !endsAt || (endsAt && endsAt === 1 && !endsAtDate))
		) {
			isRightButtonDisabled = true;
		}

		const frequencyObj: IManualBookingFrequency = frequency ? JSON.parse(frequency) : null;

		const appointmentFields = (
			<>
				<div className="row">
					<div className="col-md-8 col-12">
						<div className="recurring-event-screen__dateFieldWrapper">
							<Field
								name={'dateOfTheAppointment'}
								type="text"
								component={BookingDatePicker}
								label={t('Shop_Appointments_DateOfTheAppointment')}
								information={t('Shop_Appointments_DateOfTheAppointment')}
								validate={[required]}
								hideYearArrows={true}
								timezone={timezone}
								onChange={(_event, newValue) => this.handleDateTimeChange(newValue, 'date')}
								minDate={momentWithTimezone(timezone).startOf('day').toDate()} // cannot create appointment in the past
							/>
						</div>
					</div>
					<div className="col-md-4 col-12">
						<div className="recurring-event-screen__timeFieldWrapper">
							<Field
								name={'startTimeOfTheAppointment'}
								type="text"
								component={BookingDatePicker}
								information={t('Shop_Appointments_TimeOfTheAppointment')}
								label={t('Shop_Appointments_TimeOfTheAppointment')}
								// validate={validateStartTime}
								{...timePickerProps(moment().startOf('day').toDate(), moment().endOf('day').toDate())}
								timezone={timezone}
								onChange={(_event, newValue) => this.handleDateTimeChange(newValue, 'time')}
							/>
						</div>
					</div>
				</div>
			</>
		);

		const frequencyAndDuration = (
			<>
				<div className="row">
					<div className="col-12">
						<h3 className="recurring-event-screen__sectionTitle">
							{t('ManualBookingModal_RecurringEvent_FrequencyAndDuration')}
						</h3>
					</div>
					<div className="col-md-6 col-12">
						<div className="recurring-event-screen__fieldWrapper">
							<Field
								name={'frequency'}
								type="text"
								component={BookingSelectMenu}
								label={t('ManualBookingModal_RecurringEvent_Frequency')}
								information={t('ManualBookingModal_RecurringEvent_Frequency')}
								validate={[required]}
								items={generateFrequencyItems()}
								isRequired={true}
								searchable={false}
								dontCaptureMenuScroll={true}
							/>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="recurring-event-screen__fieldWrapper">
							<Field
								name={'endsAt'}
								type="text"
								component={BookingSelectMenu}
								label={t('ManualBookingModal_RecurringEvent_Ends')}
								information={t('ManualBookingModal_RecurringEvent_Ends')}
								validate={[required]}
								items={generateEndItems()}
								isRequired={true}
								searchable={false}
								dontCaptureMenuScroll={true}
							/>
						</div>
					</div>
					{currentFormValues?.endsAt === 1 && (
						<div className="col-12">
							<div className="recurring-event-screen__fieldWrapper">
								<Field
									name={'endsAtDate'}
									type="text"
									component={BookingDatePicker}
									label={t('Shop_WorkTime_EndDate')}
									information={t('Shop_WorkTime_EndDate')}
									validate={[required]}
									timezone={timezone}
									minDate={
										new Date(currentFormValues?.dateOfTheAppointment || momentWithTimezone(timezone).toISOString())
									}
									// Limit max date by multiplier and how many booking items
									maxDate={moment(currentFormValues?.dateOfTheAppointment)
										.add(
											frequencyObj?.multiplier * (300 / (currentFormValues?.bookingItems?.length || 1)),
											getTimeUnit(frequencyObj),
										)
										.toDate()}
								/>
							</div>
						</div>
					)}
				</div>
			</>
		);

		return (
			<RightSideModal
				size="small"
				header={
					<>
						<RightSideModalTitle
							title={t('ManualBookingModal_RecurringEvent_Main_Title')}
							closeModalClick={
								(this.props.mode === 'add' && !this.props.dirty) ||
								(this.props?.mode === 'edit' && !this.props?.currentFormValues?.isEditModeEnabled)
									? this.props.closeModal
									: this.props.startCloseModal
							}
							backButtonClick={() => {
								if (this.state?.initialRecurringEventFrequency !== this.props?.currentFormValues?.frequency) {
									this.props.change('frequency', this.state.initialRecurringEventFrequency);
								}
								if (this?.state?.initialRecurringEventEndsAt !== this.props?.currentFormValues?.endsAt) {
									this.props.change('endsAt', this.state.initialRecurringEventEndsAt);
								}
								if (
									this?.state?.initialStartTime !== this.props?.currentFormValues?.startTimeOfTheAppointment ||
									this?.state?.initialStartTime !== this.props?.currentFormValues?.dateOfTheAppointment
								) {
									this.props.change('dateOfTheAppointment', this.state.initialStartTime);
									this.props.change('startTimeOfTheAppointment', this.state.initialStartTime);
								}
								this.props.setScreen(ManualBookingScreen.Main);
							}}
						/>
					</>
				}
				footer={
					<>
						<RightSideModalFooter
							rightButtonClick={() => {
								// Clear endsAtDate if the endsAt value is not a specific date
								if (currentFormValues?.endsAtDate && currentFormValues?.endsAt !== 1) {
									this.props.change('endsAtDate', '');
								}

								if (
									currentFormValues?.frequency &&
									(JSON.parse(currentFormValues.frequency) as IManualBookingFrequency).period ===
										ManualBookingPeriods.DoesNotRepeat
								) {
									this.props.change('frequency', '');
									this.props.change('endsAt', '');
									this.props.change('endsAtDate', '');
									this.props.change('lastTime', '');
								}
								this.props.setScreen(ManualBookingScreen.Main);
							}}
							rightButtonText={t('ManualBookingModal_RecurringEvent_ApplyChanges')}
							isRightButtonDisabled={isRightButtonDisabled}
						/>
					</>
				}
			>
				{appointmentFields}
				{frequencyAndDuration}
			</RightSideModal>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: IManualBookingFormData = null;
	const currentOffice = state?.manageShop?.shop?.offices.find((off) => off.id === ownProps.officeId) || null;

	if (state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN] && state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values) {
		currentFormValues = state.form[c.FORM_NAME_MANUAL_BOOKING_PLAN].values;
	}

	return {
		currentFormValues,
		shopcard: state.shopcard,
		manageShop: state.manageShop,
		serviceGroups: state.serviceGroups,
		shopBookingTemplates: state.shopBookingTemplates,
		currentOffice,
		shopOrderCard: state.shopOrderCard,
	};
};

export const RecurringEventScreen = connect(stateToProps)(
	reduxForm({
		form: c.FORM_NAME_MANUAL_BOOKING_PLAN,
		enableReinitialize: false,
		destroyOnUnmount: false,
	})(RecurringEventScreenComponent),
);
